import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getPermissions } from '@/lib/services/rbacService'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Search, Shield } from 'lucide-react'
import type { Permission } from '@/lib/types/api'

interface PermissionSelectorProps {
  selectedPermissionIds: string[]
  onPermissionsChange: (permissionIds: string[]) => void
  disabled?: boolean
}

export function PermissionSelector({
  selectedPermissionIds,
  onPermissionsChange,
  disabled = false,
}: PermissionSelectorProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  // Fetch all permissions
  const { data: permissionsData, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => getPermissions(),
  })

  const permissions = useMemo(() => permissionsData?.permissions ?? [], [permissionsData])

  // Group permissions by code prefix (first part before dot)
  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, Permission[]> = {}

    permissions.forEach(permission => {
      const group = permission.code.split('.')[0] || 'other'
      if (!grouped[group]) {
        grouped[group] = []
      }
      grouped[group].push(permission)
    })

    return grouped
  }, [permissions])

  // Filter permissions based on search and group
  const filteredGroupedPermissions = useMemo(() => {
    const filtered: Record<string, Permission[]> = {}

    Object.entries(groupedPermissions).forEach(([group, perms]) => {
      // Filter by selected group
      if (selectedCategory !== 'all' && group !== selectedCategory) {
        return
      }

      // Filter by search query
      const matchingPerms = perms.filter(perm => {
        const searchLower = searchQuery.toLowerCase()
        return (
          perm.code.toLowerCase().includes(searchLower) ||
          (perm.description || '').toLowerCase().includes(searchLower)
        )
      })

      if (matchingPerms.length > 0) {
        filtered[group] = matchingPerms
      }
    })

    return filtered
  }, [groupedPermissions, searchQuery, selectedCategory])

  const handleTogglePermission = (permissionId: string) => {
    if (disabled) return

    // Find the permission to check if it's essential
    const permission = permissions.find(p => p.id === permissionId)
    if (permission?.isEssential) {
      return // Cannot toggle essential permissions
    }

    const newSelected = selectedPermissionIds.includes(permissionId)
      ? selectedPermissionIds.filter(id => id !== permissionId)
      : [...selectedPermissionIds, permissionId]

    onPermissionsChange(newSelected)
  }

  const handleToggleGroup = (group: string) => {
    if (disabled) return

    const groupPermissions = groupedPermissions[group] || []
    const groupPermissionIds = groupPermissions.map(p => p.id)
    const allSelected = groupPermissionIds.every(id => selectedPermissionIds.includes(id))

    let newSelected: string[]
    if (allSelected) {
      // Deselect all in group
      newSelected = selectedPermissionIds.filter(id => !groupPermissionIds.includes(id))
    } else {
      // Select all in group
      const toAdd = groupPermissionIds.filter(id => !selectedPermissionIds.includes(id))
      newSelected = [...selectedPermissionIds, ...toAdd]
    }

    onPermissionsChange(newSelected)
  }

  const getGroupSelectionState = (group: string): 'all' | 'some' | 'none' => {
    const groupPermissions = groupedPermissions[group] || []
    const groupPermissionIds = groupPermissions.map(p => p.id)
    const selectedCount = groupPermissionIds.filter(id => selectedPermissionIds.includes(id)).length

    if (selectedCount === groupPermissionIds.length) return 'all'
    if (selectedCount > 0) return 'some'
    return 'none'
  }

  const translateGroupName = (group: string): string => {
    const translationKey = `rbac.permissionGroups.${group}`
    const translated = t(translationKey)
    // If translation key not found, return the group name capitalized
    if (translated === translationKey) {
      return group.charAt(0).toUpperCase() + group.slice(1)
    }
    return translated
  }

  if (isLoadingPermissions) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <span className="sr-only">{t('common.loading')}</span>
      </div>
    )
  }

  const totalSelected = selectedPermissionIds.length
  const totalPermissions = permissions.length

  return (
    <div className="space-y-3">
      {/* Header with stats */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">
            {t('rbac.permissionsSelected', { selected: totalSelected, total: totalPermissions })}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex align-stretch gap-2 mb-3">
        <div className="w-[280px]">
          <Select value={selectedCategory} onValueChange={setSelectedCategory} disabled={disabled}>
            <SelectTrigger
              className="w-[180px] h-full text-sm"
              aria-label={t('rbac.filterByGroup')}
            >
              <SelectValue placeholder={t('rbac.allGroups')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('rbac.allGroups')}</SelectItem>
              {Object.entries(groupedPermissions).map(([group, perms]) => (
                <SelectItem key={group} value={group}>
                  {translateGroupName(group)} ({perms.length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex relative w-full bg-white">
          <Search
            className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder={t('rbac.searchPermissions')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-7 h-8 text-sm"
            disabled={disabled}
            aria-label={t('rbac.searchPermissions')}
          />
        </div>
      </div>

      {/* Permission list - compact table style */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="max-h-[600px] overflow-y-auto">
          {Object.entries(filteredGroupedPermissions).map(([group, perms]) => {
            const selectionState = getGroupSelectionState(group)

            return (
              <div key={group} className="border-2">
                {/* Group header row */}
                <div className="bg-[#1f2937] text-white border-b-1 px-3 py-2 flex items-center gap-2 sticky top-0 z-10">
                  <Checkbox
                    id={`group-${group}`}
                    checked={selectionState === 'all'}
                    onCheckedChange={() => handleToggleGroup(group)}
                    disabled={disabled}
                    aria-label={t('rbac.toggleGroupPermissions', {
                      group: translateGroupName(group),
                    })}
                    className={
                      selectionState === 'some' ? 'data-[state=checked]:bg-primary/50' : ''
                    }
                  />
                  <span className="text-md font-semibold flex-1">{translateGroupName(group)}</span>
                  <span className="text-md text-muted-foreground">
                    {perms.filter(p => selectedPermissionIds.includes(p.id)).length}/{perms.length}
                  </span>
                </div>

                {/* Individual permissions */}
                {perms.map(permission => (
                  <div
                    key={permission.id}
                    className={`px-3 py-2 border-b last:border-b-0 ${permission.isEssential ? 'bg-blue-50/30' : 'hover:bg-accent/30'} transition-colors`}
                  >
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id={permission.id}
                        checked={selectedPermissionIds.includes(permission.id)}
                        onCheckedChange={() => handleTogglePermission(permission.id)}
                        disabled={disabled || permission.isEssential}
                        aria-label={permission.code}
                        className="h-3 w-3 mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs flex items-center gap-1.5">
                          <span className="truncate">{permission.code}</span>
                          {permission.isEssential && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0">
                              Essential
                            </span>
                          )}
                          {permission.isSystem && !permission.isEssential && (
                            <Shield
                              className="h-2.5 w-2.5 flex-shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {permission.description || t('common.noDescription')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })}

          {Object.keys(filteredGroupedPermissions).length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <p>{t('rbac.noPermissionsFound')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
